export const EMPTY_DATE = "00.00.0000";

export const formatDate = (value) => {
  if (!value) return EMPTY_DATE;
  const str = String(value).trim();
  if (!str || str.startsWith("0000") || str === "null") return EMPTY_DATE;

  if (/^\d{2}\.\d{2}\.\d{4}$/.test(str)) return str;

  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}.${iso[2]}.${iso[1]}`;

  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return EMPTY_DATE;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

export const formatDateRange = (start, end) =>
  `${formatDate(start)}-${formatDate(end)}`;

export const formatPercent = (value, digits = 1) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "0%";
  const fixed = Number.isInteger(num) ? String(num) : num.toFixed(digits);
  return `${String(fixed).replace(".", ",")}%`;
};

export const clampPercent = (value) =>
  Math.max(0, Math.min(100, Number(value) || 0));

/** Разница в днях: fact - plan. Положительное = отставание. */
export const getDelayDays = (fact, plan) => {
  if (!fact || !plan) return null;
  const f = new Date(fact);
  const p = new Date(plan);
  if (Number.isNaN(f.getTime()) || Number.isNaN(p.getTime())) return null;
  const diff = Math.round((f.getTime() - p.getTime()) / (1000 * 60 * 60 * 24));
  return diff === 0 ? null : diff;
};

export const formatDelayBadge = (days) => {
  if (days == null || days === 0) return null;
  const abs = Math.abs(days);
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  let word = "дней";
  if (mod10 === 1 && mod100 !== 11) word = "день";
  else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) word = "дня";
  const sign = days > 0 ? "+" : "";
  return `${sign}${days} ${word}`;
};

export const getPersonDisplayName = (person) => {
  if (!person) return "—";
  if (person.display_name) return person.display_name;
  const full = `${person.surname || ""} ${person.name || ""}`.trim();
  if (!full) return "—";
  // Иванов И.
  const initial = person.name ? `${person.name[0]}.` : "";
  return `${person.surname || ""} ${initial}`.trim() || full;
};

export const getProjectLabel = (project) => {
  if (!project) return "";
  if (project.name) return project.name;
  const parts = [project.cipher_pilot, project.cipher_fin, project.building_type].filter(
    Boolean,
  );
  return parts.join("_") || `Проект #${project.id}`;
};

export const PROJECT_LEVEL_SECTION_ID = "project_level";
export const PROJECT_LEVEL_ALL_KEY = "project_level_all";
export const PROJECT_LEVEL_SECTION_NAME = "Вне разделов";
export const PROJECT_LEVEL_PLACEMENT_START = "start";
export const PROJECT_LEVEL_PLACEMENT_END = "end";

const PROJECT_LEVEL_NAME_RE =
  /^@@PLG\|(start|end)\|([^|]*)\|([^@]*)@@([\s\S]*)$/;

export const isProjectLevelSection = (section) =>
  Boolean(section?.is_project_level) ||
  section?.id === PROJECT_LEVEL_SECTION_ID;

export const createProjectLevelGroupId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `plg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export const normalizeProjectLevelPlacement = (placement) =>
  placement === PROJECT_LEVEL_PLACEMENT_END
    ? PROJECT_LEVEL_PLACEMENT_END
    : PROJECT_LEVEL_PLACEMENT_START;

export const encodeProjectLevelName = ({
  placement = PROJECT_LEVEL_PLACEMENT_START,
  groupId,
  groupName,
  visibleName = "",
}) => {
  const encodedGroup = encodeURIComponent(
    String(groupName || "").trim() || PROJECT_LEVEL_SECTION_NAME,
  );
  return `@@PLG|${normalizeProjectLevelPlacement(placement)}|${String(
    groupId || PROJECT_LEVEL_SECTION_ID,
  )}|${encodedGroup}@@${visibleName ?? ""}`;
};

export const parseProjectLevelName = (name) => {
  const raw = String(name || "");
  const match = raw.match(PROJECT_LEVEL_NAME_RE);
  if (!match) {
    return {
      encoded: false,
      placement: PROJECT_LEVEL_PLACEMENT_START,
      groupId: PROJECT_LEVEL_SECTION_ID,
      groupName: PROJECT_LEVEL_SECTION_NAME,
      visibleName: raw,
      isSentinel: false,
    };
  }
  let groupName = match[3];
  try {
    groupName = decodeURIComponent(groupName);
  } catch {
    // оставляем как есть, если бэкенд вернул уже декодированную строку
  }
  const visibleName = match[4] ?? "";
  return {
    encoded: true,
    placement: normalizeProjectLevelPlacement(match[1]),
    groupId: match[2] || PROJECT_LEVEL_SECTION_ID,
    groupName: String(groupName || "").trim() || PROJECT_LEVEL_SECTION_NAME,
    visibleName,
    isSentinel: visibleName === "",
  };
};

export const getProjectLevelVisibleName = (name) => {
  const parsed = parseProjectLevelName(name);
  return parsed.encoded ? parsed.visibleName : parsed.visibleName;
};

export const buildProjectLevelStoredName = (section, visibleName = "") =>
  encodeProjectLevelName({
    placement: section?.placement,
    groupId: section?.id,
    groupName: section?.name,
    visibleName,
  });

export const getProjectLevelWorkTypes = (payload) =>
  Array.isArray(payload?.project_work_types) ? payload.project_work_types : [];

export const groupProjectLevelWorkTypes = (workTypes = []) => {
  const groups = [];
  const byId = new Map();
  const list = Array.isArray(workTypes) ? [...workTypes] : [];
  list.sort(
    (a, b) =>
      (a.order_index ?? 0) - (b.order_index ?? 0) || (a.id || 0) - (b.id || 0),
  );

  list.forEach((workType) => {
    const meta = parseProjectLevelName(workType?.name);
    const groupId = meta.groupId || PROJECT_LEVEL_SECTION_ID;
    if (!byId.has(groupId)) {
      const group = {
        id: groupId,
        name: meta.groupName,
        placement: meta.placement,
        items: [],
        allItems: [],
      };
      byId.set(groupId, group);
      groups.push(group);
    }
    const group = byId.get(groupId);
    group.allItems.push(workType);
    if (meta.encoded) {
      group.name = meta.groupName;
      group.placement = meta.placement;
    }
    if (!meta.isSentinel) {
      group.items.push({
        ...workType,
        name: meta.visibleName || workType.name,
      });
    }
  });

  return groups;
};

export const withProjectLevelRelationSection = (data) => {
  const catalog = Array.isArray(data?.sections) ? [...data.sections] : [];
  const groups = groupProjectLevelWorkTypes(getProjectLevelWorkTypes(data));
  const toSection = (group) => ({
    id: group.id,
    name: group.name,
    placement: group.placement,
    is_project_level: true,
    work_types: group.items,
  });
  return [
    ...groups
      .filter((group) => group.placement !== PROJECT_LEVEL_PLACEMENT_END)
      .map(toSection),
    ...catalog,
    ...groups
      .filter((group) => group.placement === PROJECT_LEVEL_PLACEMENT_END)
      .map(toSection),
  ];
};

/** Короткий код раздела из имени (ГП, АР) — если имя длинное, берём первые буквы/токен */
export const splitSectionName = (name) => {
  if (!name) return { code: "—", title: "" };
  const trimmed = String(name).trim();
  // "ГП — Генеральный план" / "ГП - Генеральный план" / "ГП: Генеральный план"
  const split = trimmed.match(/^([A-Za-zА-Яа-яЁё0-9/]+)\s*[—\-:|]\s*(.+)$/);
  if (split) {
    return { code: split[1], title: split[2] };
  }
  // Короткий код без описания
  if (trimmed.length <= 8 && !/\s/.test(trimmed)) {
    return { code: trimmed, title: "" };
  }
  return { code: trimmed, title: "" };
};

export const getScheduleSectionLabel = (section) => {
  if (isProjectLevelSection(section)) {
    return section?.name || PROJECT_LEVEL_SECTION_NAME;
  }
  const { code, title } = splitSectionName(section?.name);
  return title ? `${code} — ${title}` : code;
};

export const getScheduleSectionCode = (section) => {
  if (isProjectLevelSection(section)) {
    return section?.name || PROJECT_LEVEL_SECTION_NAME;
  }
  return splitSectionName(section?.name).code;
};

/** yyyy-mm-dd для input[type=date] */
export const toDateInputValue = (value) => {
  if (!value) return "";
  const str = String(value).trim();
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const dmy = str.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${yyyy}-${mm}-${dd}`;
};

export const calcDurationDays = (start, end) => {
  if (!start || !end) return null;
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;
  return Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
};

export const formatDaysWord = (days) => {
  if (days == null || Number.isNaN(days)) return "—";
  const abs = Math.abs(days);
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  let word = "дней";
  if (mod10 === 1 && mod100 !== 11) word = "день";
  else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) word = "дня";
  return `${days} ${word}`;
};

/**
 * Определяет статус задачи/вехи (без просрочки):
 * - "completed" — завершена
 * - "in_progress" — начата, не завершена
 * - "pending" — ещё не начата
 * Просрочка — отдельно через isWorkTypeOverdue (подсветка строки).
 */
export const getWorkTypeStatus = (wt) => {
  if (wt.is_completed || clampPercent(wt.completion_percent) >= 100) {
    return "completed";
  }
  if (wt.is_milestone && (wt.fact_start_date || wt.fact_end_date)) {
    return "completed";
  }
  if (wt.fact_start_date || wt.fact_end_date) return "in_progress";
  return "pending";
};

/** Просрочена: плановый срок истёк и работа/веха ещё не завершена */
export const isWorkTypeOverdue = (wt) => {
  if (!wt) return false;
  if (wt.is_completed || clampPercent(wt.completion_percent) >= 100) return false;
  if (wt.is_milestone && (wt.fact_start_date || wt.fact_end_date)) return false;
  const endDate = wt.plan_end_date || wt.plan_start_date;
  if (!endDate) return false;
  const end = new Date(endDate);
  if (Number.isNaN(end.getTime())) return false;
  return end < new Date().setHours(0, 0, 0, 0);
};

export const getWorkTypesDateRange = (workTypes, startKey, endKey) => {
  if (!Array.isArray(workTypes) || workTypes.length === 0) {
    return { start: null, end: null };
  }

  let start = null;
  let end = null;
  for (const wt of workTypes) {
    const wtStart = wt[startKey] || wt[endKey];
    const wtEnd = wt[endKey] || wt[startKey];
    if (wtStart && (!start || String(wtStart) < String(start))) start = wtStart;
    if (wtEnd && (!end || String(wtEnd) > String(end))) end = wtEnd;
  }
  return { start, end };
};

/** Фактический срок раздела: из API или агрегация по видам работ/вехам */
export const getSectionFactDateRange = (section, workTypes) => {
  if (section?.fact_start_date || section?.fact_end_date) {
    return {
      start: section.fact_start_date || null,
      end: section.fact_end_date || null,
    };
  }

  return getWorkTypesDateRange(workTypes, "fact_start_date", "fact_end_date");
};

const resolveWorkTypeRelationId = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "object") {
    return value.id != null ? Number(value.id) : null;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

/** predecessor_ids / successor_ids из ответа API (массив или legacy singular) */
export const resolveWorkTypeRelationIds = (workType, field) => {
  const pluralKey = `${field}_ids`;
  const singularKey = `${field}_id`;

  if (Array.isArray(workType?.[pluralKey])) {
    return workType[pluralKey]
      .map((id) => resolveWorkTypeRelationId(id))
      .filter((id) => id != null);
  }

  const single = resolveWorkTypeRelationId(
    workType?.[singularKey] ?? workType?.[field],
  );
  return single != null ? [single] : [];
};
